/**
 * @fileoverview Utility functions for handling select component operations in the pitch form.
 * This module provides utilities for managing genre selections, formatting options, and 
 * handling multi-select operations in the PitchBook application.
 * 
 * @module PitchFormUtils
 * @requires DropdownOptions
 * @requires DSPPlatform
 * @requires MainGenre
 * @requires SelectOption
 * 
 * @example
 * // Example usage of getSubgenreOptions
 * const dsp = 'spotify';
 * const mainGenres = ['Rock', 'Pop'];
 * const subgenres = getSubgenreOptions(dsp, mainGenres);
 * 
 * @example
 * // Example usage of handleMultiSelect
 * const currentSelection = ['rock', 'pop'];
 * const newSelection = handleMultiSelect(currentSelection, 'jazz');
 * // Result: ['rock', 'pop', 'jazz']
 */

import { DSPPlatform, MainGenre, SelectOption } from '../types/dropdownTypes';
import { dspSubgenres } from '../Data/dropdownOptions';

/**
 * Type definition for the subgenre mapping structure.
 * Represents the hierarchical relationship between DSPs, main genres, and their subgenres.
 * 
 * @typedef {Object} SubgenreMap
 * @property {Object} DSPPlatform - Platform-specific genre mappings
 * @property {string[]} DSPPlatform[MainGenre] - Array of subgenres for each main genre
 * 
 * @example
 * // Structure example:
 * {
 *   spotify: {
 *     Rock: ['Alternative Rock', 'Classic Rock', ...],
 *     Pop: ['Dance Pop', 'Indie Pop', ...]
 *   },
 *   amazon: {
 *     // Similar structure for Amazon
 *   }
 * }
 */
type SubgenreMap = {
  [P in DSPPlatform]: {
    [G in MainGenre]?: string[];
  };
};

/**
 * Type guard function to validate if a string is a valid MainGenre.
 * Uses the Spotify genre list as the source of truth for valid main genres.
 * 
 * @param {string} genre - The genre string to validate
 * @returns {boolean} True if the genre is a valid MainGenre, false otherwise
 * 
 * @example
 * if (isValidMainGenre('Rock')) {
 *   // Handle valid genre
 * } else {
 *   // Handle invalid genre
 * }
 */
const isValidMainGenre = (genre: string): genre is MainGenre => {
  return genre in (dspSubgenres['spotify'] || {});
};

/**
 * Retrieves and processes subgenre options for a specific DSP and set of main genres.
 * This function handles the core logic for generating subgenre lists based on main genre selections.
 * 
 * @param {DSPPlatform} dsp - The digital service provider platform (e.g., 'spotify', 'amazon')
 * @param {string[]} mainGenres - Array of main genre categories selected by the user
 * @returns {string[]} Array of unique subgenres sorted alphabetically
 * 
 * @throws {Error} Will return an empty array if dsp is invalid or mainGenres is not an array
 * 
 * @example
 * // Get subgenres for Spotify Rock and Pop selections
 * const subgenres = getSubgenreOptions('spotify', ['Rock', 'Pop']);
 * 
 * @example
 * // Handle invalid input gracefully
 * const emptyResult = getSubgenreOptions(null, []); // Returns []
 */
export const getSubgenreOptions = (
  dsp: DSPPlatform,
  mainGenres: string[]
): string[] => {
  // Early return for invalid inputs
  if (!dsp || !Array.isArray(mainGenres)) return [];

  // Convert and validate main genres
  const validMainGenres = mainGenres.filter(isValidMainGenre);
  
  const dspData = dspSubgenres[dsp] as SubgenreMap[DSPPlatform];
  if (!dspData) return [];

  // Flatten and deduplicate subgenres
  const allSubgenres = validMainGenres.flatMap((genre) => {
    const subgenres = dspData[genre];
    return subgenres ? Array.from(subgenres) : [];
  });

  return [...new Set(allSubgenres)].sort();
};

/**
 * Formats an array of option strings into select component compatible objects.
 * Transforms simple strings into objects with value and label properties,
 * where values are kebab-cased and labels maintain original formatting.
 * 
 * @param {string[]} options - Array of option strings to format
 * @returns {SelectOption[]} Array of formatted select options
 * 
 * @example
 * const options = ['Alternative Rock', 'Pop Rock'];
 * const formatted = formatOptionsForSelect(options);
 * // Result: [
 * //   { value: 'alternative-rock', label: 'Alternative Rock' },
 * //   { value: 'pop-rock', label: 'Pop Rock' }
 * // ]
 */
export const formatOptionsForSelect = (options: string[]): SelectOption[] => {
  return options.map(option => ({
    value: option.toLowerCase().replace(/\s+/g, '-'),
    label: option
  }));
};

/**
 * Handles multi-select operations by toggling values in an array.
 * If the value exists in the array, it's removed; if it doesn't exist, it's added.
 * This function maintains immutability by returning a new array.
 * 
 * @param {string[]} currentValues - Array of currently selected values
 * @param {string} newValue - Value to toggle (add or remove)
 * @returns {string[]} New array with updated selections
 * 
 * @example
 * // Adding a new value
 * const current = ['rock', 'pop'];
 * const updated = handleMultiSelect(current, 'jazz');
 * // Result: ['rock', 'pop', 'jazz']
 * 
 * @example
 * // Removing an existing value
 * const updated = handleMultiSelect(['rock', 'pop'], 'rock');
 * // Result: ['pop']
 */
export const handleMultiSelect = (
  currentValues: string[],
  newValue: string
): string[] => {
  if (currentValues.includes(newValue)) {
    return currentValues.filter(value => value !== newValue);
  }
  return [...currentValues, newValue];
};

/**
 * Converts an array of strings to an array of validated MainGenres.
 * Filters out any invalid genre strings, ensuring type safety for genre operations.
 * 
 * @param {string[]} genres - Array of genre strings to validate and convert
 * @returns {MainGenre[]} Array of validated MainGenres
 * 
 * @example
 * const genres = ['Rock', 'Invalid', 'Pop'];
 * const mainGenres = convertToMainGenres(genres);
 * // Result: ['Rock', 'Pop']
 * 
 * @example
 * // Handle empty or invalid input
 * const empty = convertToMainGenres([]);
 * // Result: []
 */
export const convertToMainGenres = (genres: string[]): MainGenre[] => {
  return genres.filter(isValidMainGenre);
};

/**
 * Type guard to check if a platform is a valid DSP
 * @param {string} platform - The platform to validate
 * @returns {boolean} Whether the platform is a valid DSP
 */
export const isValidDSPPlatform = (platform: string): platform is DSPPlatform => {
  return ['spotify', 'amazon', 'deezer', 'audiomack', 'TIDAL','soundcloud'].includes(platform);
};